import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { apiGet, prettifyApiError } from "@/shared/api";
import { formatDateIt, formatMoney } from "@/shared/format";
import { MarketplaceLogo } from "@/shared/ui/MarketplaceLogo";
import { useDebounced } from "@/shared/useDebounced";
import { mapOrder } from "@/features/ordini/api";
import { orderMarketplaceLogo } from "@/features/ordini/components/OrderCard";
import type { Order } from "@/features/ordini/types";
import { navItems, nomeVoce, type NavItem } from "./Rail";
import { IC_CLASS, RailButton } from "./RailButton";

/**
 * «Cerca» (proposta menu 2026-10-28): da qualunque pagina, Ctrl+K (⌘K sul Mac)
 * o il tasto in cima al menu apre un campo solo che trova due cose:
 *  - un ORDINE: numero, cliente, telefono, tracking, SKU — la stessa ricerca
 *    della lista Ordini (`GET /api/orders?q=`, che ignora la finestra di date);
 *  - una PAGINA del menu, per nome.
 * Invio apre il primo risultato. Prima per aprire un ordine visto in una mail
 * bisognava andare su Ordini, svuotare i filtri e scrivere nel loro campo.
 */

const EVENTO_APRI = "om:cerca-rapida";
const MIN_CARATTERI = 3;
const MAX_ORDINI = 6;

/** Apre la ricerca da un tasto qualsiasi (rail, barra del telefono). */
export function apriCercaRapida(): void {
  window.dispatchEvent(new Event(EVENTO_APRI));
}

function IconaCerca({ className = IC_CLASS }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

/** Tasto in cima al rail. */
export function RailCercaButton() {
  return (
    <RailButton
      ariaLabel="Cerca un ordine o una pagina (Ctrl+K)"
      label="Cerca"
      onClick={apriCercaRapida}
      icon={<IconaCerca />}
    />
  );
}

/** Tasto tondo della barra in alto del telefono. */
export function TastoCercaBarra() {
  return (
    <button
      type="button"
      aria-label="Cerca un ordine o una pagina"
      onClick={apriCercaRapida}
      className="om-press grid h-11 w-11 place-items-center rounded-full text-ink active:bg-paper-2"
    >
      <IconaCerca className="h-[22px] w-[22px] fill-none stroke-current stroke-[1.8px] [stroke-linecap:round] [stroke-linejoin:round]" />
    </button>
  );
}

/** Tutte le pagine raggiungibili, sotto-voci comprese. Funzione e non
 *  costante: Rail importa questo file e questo file importa Rail, e una
 *  costante calcolata al caricamento troverebbe `navItems` non ancora pronto. */
function tuttePagine(): NavItem[] {
  return navItems.flatMap((i) => i.children ?? [i]);
}

function normalizza(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

export function pagineCheCombaciano(testo: string): NavItem[] {
  const q = normalizza(testo.trim());
  if (!q) return tuttePagine();
  return tuttePagine().filter(
    (p) =>
      normalizza(`${nomeVoce(p)} ${p.label}`)
        .split(/\s+/)
        .some((parola) => parola.startsWith(q)) || normalizza(nomeVoce(p)).startsWith(q),
  );
}

type ApiOrderList = { items: Parameters<typeof mapOrder>[0][] };

type StatoOrdini =
  | { fase: "corto" }
  | { fase: "cerco" }
  | { fase: "pronti"; ordini: Order[] }
  | { fase: "errore"; messaggio: string };

function useOrdiniCercati(testo: string, attivo: boolean): StatoOrdini {
  const q = useDebounced(testo.trim(), 250);
  const [stato, setStato] = useState<StatoOrdini>({ fase: "corto" });

  useEffect(() => {
    if (!attivo || q.length < MIN_CARATTERI) {
      setStato({ fase: "corto" });
      return;
    }
    const ctrl = new AbortController();
    setStato({ fase: "cerco" });
    const params = new URLSearchParams({ q, limit: String(MAX_ORDINI) });
    apiGet<ApiOrderList>(`/api/orders?${params.toString()}`, { signal: ctrl.signal })
      .then((r) => setStato({ fase: "pronti", ordini: r.items.map(mapOrder) }))
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        setStato({ fase: "errore", messaggio: prettifyApiError(err) });
      });
    return () => ctrl.abort();
  }, [q, attivo]);

  return stato;
}

type Risultato =
  | { tipo: "ordine"; chiave: string; ordine: Order }
  | { tipo: "pagina"; chiave: string; pagina: NavItem };

/** Montato una volta in AppShell: ascolta Ctrl+K e i tasti «Cerca». */
export function CercaRapida() {
  const [aperta, setAperta] = useState(false);

  useEffect(() => {
    const apri = (): void => setAperta(true);
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setAperta((v) => !v);
      }
    };
    window.addEventListener(EVENTO_APRI, apri);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener(EVENTO_APRI, apri);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  if (!aperta) return null;
  return <PannelloCerca onClose={() => setAperta(false)} />;
}

function PannelloCerca({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [testo, setTesto] = useState("");
  const [indice, setIndice] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listaId = useId();
  const ordini = useOrdiniCercati(testo, true);

  useEffect(() => {
    inputRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const risultati: Risultato[] = useMemo(() => {
    const r: Risultato[] = [];
    if (ordini.fase === "pronti") {
      for (const o of ordini.ordini) r.push({ tipo: "ordine", chiave: `o:${o.id}`, ordine: o });
    }
    for (const p of pagineCheCombaciano(testo))
      r.push({ tipo: "pagina", chiave: `p:${p.path}`, pagina: p });
    return r;
  }, [ordini, testo]);

  // Nuovi risultati = si riparte dal primo: Invio apre quello in cima.
  useEffect(() => setIndice(0), [risultati.length, testo]);

  function apri(r: Risultato | undefined): void {
    if (!r) return;
    onClose();
    void navigate(
      r.tipo === "ordine" ? `/ordini/${encodeURIComponent(r.ordine.id)}` : r.pagina.path,
    );
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndice((i) => Math.min(i + 1, risultati.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndice((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      apri(risultati[indice]);
    }
  }

  const ordiniRis = risultati.filter((r) => r.tipo === "ordine");
  const pagineRis = risultati.filter((r) => r.tipo === "pagina");
  const idOpzione = (i: number) => `${listaId}-${i}`;

  return createPortal(
    <div className="fixed inset-0 z-[9995]">
      <button
        type="button"
        aria-label="Chiudi la ricerca"
        onClick={onClose}
        className="om-fade absolute inset-0 cursor-default bg-ink/45"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Cerca"
        className={[
          "absolute flex flex-col overflow-hidden bg-card",
          // Telefono: tutto lo schermo, il campo in alto dove arriva il pollice
          // della tastiera. Computer: un pannello in alto al centro.
          "inset-0",
          "md:inset-x-0 md:bottom-auto md:top-[12vh] md:mx-auto md:max-h-[72vh] md:w-[min(640px,calc(100vw-48px))]",
          "md:rounded-lg md:border md:border-line md:shadow-popover",
        ].join(" ")}
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-line px-4 md:px-5">
          <IconaCerca className="h-5 w-5 shrink-0 fill-none stroke-ink stroke-[1.8px] [stroke-linecap:round]" />
          <input
            ref={inputRef}
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded="true"
            aria-controls={listaId}
            aria-activedescendant={risultati.length ? idOpzione(indice) : undefined}
            aria-label="Numero ordine, cliente, telefono, tracking o pagina"
            placeholder="Numero ordine, cliente, telefono, tracking…"
            className="h-[60px] min-w-0 flex-1 bg-transparent text-[16px] text-ink outline-none placeholder:text-ink-2"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            onClick={onClose}
            className="om-press -mr-2 shrink-0 rounded-md px-2 py-1.5 text-[13px] font-semibold text-ink hover:bg-paper-2"
          >
            <span className="md:hidden">Chiudi</span>
            <kbd className="hidden rounded border border-line-2 px-1.5 py-0.5 font-sans text-[11px] text-ink md:inline">
              Esc
            </kbd>
          </button>
        </div>

        <ul
          id={listaId}
          role="listbox"
          aria-label="Risultati"
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-2"
        >
          {testo.trim().length > 0 && testo.trim().length < MIN_CARATTERI && (
            <li role="presentation" className="px-5 py-2 text-[13px] text-ink">
              {MIN_CARATTERI - testo.trim().length === 1
                ? "Ancora 1 lettera"
                : `Ancora ${MIN_CARATTERI - testo.trim().length} lettere`}{" "}
              e cerco anche negli ordini.
            </li>
          )}
          {ordini.fase === "cerco" && (
            <li
              role="presentation"
              className="flex items-center gap-2 px-5 py-3 text-[13px] text-ink"
            >
              <span
                className="h-3 w-3 animate-spin rounded-full border-2 border-line-2 border-t-accent"
                aria-hidden="true"
              />
              Cerco negli ordini…
            </li>
          )}
          {ordini.fase === "errore" && (
            <li
              role="presentation"
              className="mx-4 my-1 rounded-md bg-red-soft px-3 py-2 text-[13px] text-ink"
            >
              La ricerca ordini non ha risposto: {ordini.messaggio}
            </li>
          )}
          {ordini.fase === "pronti" && ordiniRis.length === 0 && (
            <li role="presentation" className="px-5 py-3 text-[13px] text-ink">
              Nessun ordine con «{testo.trim()}».
            </li>
          )}
          {ordiniRis.length > 0 && <TitoloGruppo>Ordini</TitoloGruppo>}
          {ordiniRis.map((r) => {
            const i = risultati.indexOf(r);
            return r.tipo === "ordine" ? (
              <RigaOrdine
                key={r.chiave}
                id={idOpzione(i)}
                ordine={r.ordine}
                attiva={i === indice}
                onHover={() => setIndice(i)}
                onClick={() => apri(r)}
              />
            ) : null;
          })}
          {pagineRis.length > 0 && <TitoloGruppo>{testo.trim() ? "Pagine" : "Vai a"}</TitoloGruppo>}
          {pagineRis.map((r) => {
            const i = risultati.indexOf(r);
            return r.tipo === "pagina" ? (
              <li
                key={r.chiave}
                id={idOpzione(i)}
                role="option"
                aria-selected={i === indice}
                onMouseMove={() => setIndice(i)}
                onClick={() => apri(r)}
                className={[
                  "mx-2 flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-[14px] font-semibold text-ink",
                  i === indice ? "bg-paper-2" : "",
                ].join(" ")}
              >
                <span className="text-ink">{r.pagina.icon}</span>
                {nomeVoce(r.pagina)}
              </li>
            ) : null;
          })}
        </ul>

        <div className="hidden shrink-0 items-center gap-4 border-t border-line bg-paper px-5 py-2 text-[12px] text-ink md:flex">
          <span>
            <Tasto>Invio</Tasto> apre
          </span>
          <span>
            <Tasto>↑</Tasto> <Tasto>↓</Tasto> scegli
          </span>
          <span className="ml-auto">
            <Tasto>Ctrl</Tasto> <Tasto>K</Tasto> da ogni pagina
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function TitoloGruppo({ children }: { children: string }) {
  return (
    <li role="presentation" className="px-5 pb-1 pt-3 text-[12px] font-bold text-ink">
      {children}
    </li>
  );
}

function Tasto({ children }: { children: string }) {
  return (
    <kbd className="rounded border border-line-2 bg-card px-1.5 py-0.5 font-sans text-[11px] text-ink">
      {children}
    </kbd>
  );
}

function RigaOrdine({
  id,
  ordine,
  attiva,
  onHover,
  onClick,
}: {
  id: string;
  ordine: Order;
  attiva: boolean;
  onHover: () => void;
  onClick: () => void;
}) {
  return (
    <li
      id={id}
      role="option"
      aria-selected={attiva}
      onMouseMove={onHover}
      onClick={onClick}
      className={[
        "mx-2 grid cursor-pointer grid-cols-[22px_1fr_auto] items-center gap-x-3 rounded-md px-3 py-2.5",
        attiva ? "bg-paper-2" : "",
      ].join(" ")}
    >
      <MarketplaceLogo {...orderMarketplaceLogo(ordine)} size={20} />
      <span className="min-w-0">
        <span className="block truncate text-[14px] font-bold tabular-nums text-ink">
          {ordine.id}
          <span className="ml-2 font-semibold">{ordine.customer.name}</span>
        </span>
        <span className="block truncate text-[12.5px] text-ink">{ordine.productName}</span>
      </span>
      <span className="text-right">
        <span className="block text-[13.5px] font-bold tabular-nums text-ink">
          {formatMoney(ordine.total)}
        </span>
        <span className="block text-[12px] tabular-nums text-ink">
          {formatDateIt(ordine.createdAtPoleepo)}
        </span>
      </span>
    </li>
  );
}
